import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Child } from "@/lib/supabase";
import { CHILD_COLORS } from "@/config/childColors";

// ── Fallback palette (used when child has no avatar_color set yet) ───────────

const FALLBACK_COLORS = CHILD_COLORS.map((c) => c.hex);

function hashName(name: string): number {
  if (!name) return 0;
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return sum % FALLBACK_COLORS.length;
}

// ── Module-level signed URL cache ───────────────────────────────────────────
// Survives component remounts (e.g. navigating away and back). Resets on full
// page reload, which is fine since signed URLs expire after 24h anyway.

const signedUrlCache = new Map<string, string>();

/** Extract a bare storage path from whatever is in avatar_url. */
function extractPath(url: string): string | null {
  if (!url.startsWith("http")) return url.split("?")[0];
  const marker = "/object/public/avatars/";
  const idx = url.indexOf(marker);
  if (idx !== -1) return url.slice(idx + marker.length).split("?")[0];
  return null;
}

// ── Component ───────────────────────────────────────────────────────────────

interface ChildAvatarProps {
  child: Pick<Child, "name" | "avatar_url" | "avatar_color">;
  size?: number;
  onClick?: () => void;
  showEditRing?: boolean;
  className?: string;
}

const ChildAvatar = ({
  child,
  size = 40,
  onClick,
  showEditRing,
  className,
}: ChildAvatarProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(() => {
    // Check cache synchronously on mount — avoids flash if already cached
    if (!child.avatar_url) return null;
    return signedUrlCache.get(child.avatar_url) ?? null;
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Resolve signed URL for private avatars bucket
  useEffect(() => {
    if (!child.avatar_url) {
      setSignedUrl(null);
      return;
    }

    // Already cached?
    const cached = signedUrlCache.get(child.avatar_url);
    if (cached) {
      setSignedUrl(cached);
      return;
    }

    const path = extractPath(child.avatar_url);
    if (!path) {
      // Already a signed URL or external — use directly and cache
      signedUrlCache.set(child.avatar_url, child.avatar_url);
      setSignedUrl(child.avatar_url);
      return;
    }

    let cancelled = false;
    supabase.storage
      .from("avatars")
      .createSignedUrl(path, 86400) // 24 hours
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.signedUrl) {
          signedUrlCache.set(child.avatar_url!, data.signedUrl);
          setSignedUrl(data.signedUrl);
        } else {
          setImageError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setImageError(true);
      });

    return () => { cancelled = true; };
  }, [child.avatar_url]);

  // Reset loaded/error state when URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [signedUrl]);

  const bgColor = child.avatar_color ?? FALLBACK_COLORS[hashName(child.name)];
  const letter = child.name?.charAt(0).toUpperCase() || "?";
  const fontSize = Math.max(11, Math.round(size * 0.42));

  const showImage = signedUrl && !imageError;

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: "50%",
    position: "relative",
    overflow: "hidden",
    flexShrink: 0,
    userSelect: "none",
    // Always show the child's colour ring; edit mode adds a gap to make it more prominent
    boxShadow: showEditRing
      ? `0 0 0 2px white, 0 0 0 4px ${bgColor}`
      : `0 0 0 2px ${bgColor}`,
  };

  const inner = (
    <>
      {/* Initials — always rendered as the base layer */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: bgColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <span style={{ color: "#FFFFFF", fontWeight: 700, fontSize, lineHeight: 1 }}>
          {letter}
        </span>
      </div>

      {/* Photo — fades in on top once loaded */}
      {showImage && (
        <img
          src={signedUrl}
          alt={child.name}
          draggable={false}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: imageLoaded ? 1 : 0,
            transition: "opacity 200ms ease",
          }}
        />
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={className}
        style={{ ...containerStyle, cursor: "pointer", border: "none", padding: 0 }}
      >
        {inner}
      </button>
    );
  }

  return (
    <div className={className} style={containerStyle}>
      {inner}
    </div>
  );
};

export default ChildAvatar;
